/**
 * This is a wrapper around Number.toLocaleString that polyfills a subset of the finalized
 * `Intl.NumberFormat` v3 specification. You can view the ful v3 spec at:
 * https://github.com/tc39/proposal-intl-numberformat-v3
 */
import { separatorsForLocale } from './separators-for-locale.js';
import { toNumber } from './to-number.js';
import { toSafeLocaleString } from './to-safe-locale-string.js';
/**
 * Patch support for `roundingMode: 'floor'`
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#roundingmode
 */
const patchRoundingModeFloor = {
    test: (_number, _locale, options = {}) => options.roundingMode === 'floor' &&
        options.maximumFractionDigits !== undefined,
    patchInputNumber: (number, _locale, options = {}) => {
        /**
         * @see @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#maximumfractiondigits
         */
        const maximumFractionDigits = Math.max(options.minimumFractionDigits ?? 0, options.maximumFractionDigits ?? 3);
        return (Math.floor(number * 10 ** maximumFractionDigits) /
            10 ** maximumFractionDigits);
    }
};
/**
 * Patch support for `trailingZeroDisplay: 'stripIfInteger'`
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#trailingzerodisplay
 */
const patchTrailingZeroDisplay = {
    test: (_number, _locale, options = {}) => options.trailingZeroDisplay === 'stripIfInteger',
    patchOutputString: (output) => output.replace(/[,.]0+/, '')
};
/**
 * Patch support for `signDisplay: 'negative'`
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#signdisplay
 */
const patchSignDisplayNegative = {
    test: (_number, _locale, options = {}) => options.signDisplay === 'negative',
    patchOptions: (_number, _locale, options = {}) => ({
        ...options,
        signDisplay: 'exceptZero'
    }),
    patchOutputString: (output) => output.replace('+', '')
};
/**
 * Patch support for thousands separator in select spanish locales.
 *
 * Currently (september 2022), the Unicode CLDR data for es-ES and locales that
 * inherit from es-ES does not use a thousands separator for 4-digit numbers.
 * This is in-line with the Diccionario panhispánico de dudas style guide from
 * the Spanish Royal Academy but it does *not* line up with any common language
 * usage including major periodicals and government publication.
 *
 * The CLDR data may soon be updated to reflect the common usage, but it will
 * take a while for browser vendors to pick up these changes. In the mean time,
 * we can patch this locally by specifying useGrouping: 'always' for affected
 * locales.
 */
const patchSpanishThousandsBehavior = {
    test: (_number, locale) => ['es-ES', 'pt-PT'].includes(locale),
    patchOutputString: (output, locale) => {
        const { group } = separatorsForLocale(locale);
        return output.replace(/^\d{4}/, (match) => `${match[0]}${group}${match.substring(1)}`);
    }
};
/**
 * Adds non-standard `noDecimals` support. This adds syntactic sugar for setting
 * `minimumFractionDigits` and `maximumFractionDigits` to 0.
 */
const patchNoDecimals = {
    test: (_number, _locale, options = {}) => Boolean(options.noDecimals),
    patchOptions: (_number, _locale, options = {}) => ({
        ...options,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })
};
const PATCHES = [
    patchRoundingModeFloor,
    patchTrailingZeroDisplay,
    patchSignDisplayNegative,
    patchSpanishThousandsBehavior,
    patchNoDecimals
];
export const toLocaleString = (target, locale, options = {}) => {
    const number = toNumber(target, locale);
    const patches = PATCHES.filter((patch) => patch.test(number, locale, options));
    const patchedOptions = patches
        .filter((patch) => patch.patchOptions)
        .reduce(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (options, patch) => patch.patchOptions(number, locale, options), options);
    const patchedNumber = patches
        .filter((patch) => patch.patchInputNumber)
        .reduce((number, patch) => 
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    patch.patchInputNumber(number, locale, patchedOptions), number);
    return patches
        .filter((patch) => patch.patchOutputString)
        .reduce((string, patch) => 
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    patch.patchOutputString(string, locale, patchedOptions), toSafeLocaleString(patchedNumber, locale, patchedOptions));
};
